import { useApolloClient, useQuery } from '@apollo/client';
import {
	ItemFragmentFragment,
	type NewBoardMutationMutationVariables,
} from '@apps/www/src/__generated__/graphql';
import SVInviteUsersContainer from '@apps/www/src/www/containers/SVInviteUsersContainer';
import InviteUsersToBoardMutation from '@apps/www/src/www/queries/InviteUsersToBoardMutation';
import ItemDetailsQuery from '@apps/www/src/www/queries/ItemDetailsQuery';
import NewBoardMutation from '@apps/www/src/www/queries/NewBoardMutation';
import SVButton, { SVButtonSIZES, SVButtonUSES } from '@pkgs/shared-client/components/SVButton';
import SVFormLayout from '@pkgs/shared-client/components/SVFormLayout';
import SVImage from '@pkgs/shared-client/components/SVImage';
import SVMessage from '@pkgs/shared-client/components/SVMessage';
import SVModal, { useModalClose } from '@pkgs/shared-client/components/SVModal';
import SVModalUsersList from '@pkgs/shared-client/components/SVModalUsersList';
import { toast } from '@pkgs/shared-client/components/SVToast';
import SVToggle from '@pkgs/shared-client/components/SVToggle';
import cn from '@pkgs/shared-client/helpers/cn';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import BoardOwnershipType from '@pkgs/shared/enums/BoardOwnershipType';
import InviteType from '@pkgs/shared/enums/InviteType';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';

type LikeUser = Parameters<
	NonNullable<React.ComponentProps<typeof SVModalUsersList.SelectItem>['onSelect']>
>[0];

const PLACEHOLDER_CHANGE_INTERVAL = 3000;
const _AnimatedPlaceholderText = ({ tags }: { tags: string[] }) => {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [lastIndex, setLastIndex] = useState(0);

	const PlaceholderPhrases = useMemo(() => {
		if (!tags || tags.length === 0) {
			return [];
		}
		return tags.map((tag) => `${tag}`);
	}, [tags]);

	useEffect(() => {
		setCurrentIndex(0);
		setLastIndex(0);
	}, [tags]);

	useEffect(() => {
		if (PlaceholderPhrases.length === 0) return;

		const timer = setInterval(() => {
			setLastIndex(currentIndex);
			setCurrentIndex((prevIndex) => (prevIndex + 1) % PlaceholderPhrases.length);
		}, PLACEHOLDER_CHANGE_INTERVAL);

		return () => clearInterval(timer);
	}, [currentIndex, PlaceholderPhrases.length]);

	return (
		<div className="duration-slide pointer-events-none absolute left-[18px] top-0 flex h-full w-full items-center text-gray-700 transition-[margin] ease-in-out">
			{PlaceholderPhrases.length > 0 && <span>Like&nbsp;</span>}
			<span className="relative h-[42px] flex-1 overflow-visible">
				<span className="absolute h-full w-full overflow-hidden">
					{PlaceholderPhrases.map((phrase, index) => (
						<span
							key={`${phrase}-${index}`}
							className={cn(
								'absolute flex h-full w-full items-center truncate',
								index === currentIndex
									? 'translate-y-0'
									: index === lastIndex
									? 'translate-y-[-30px]'
									: 'translate-y-[30px]',
								// Only animate current and previous phrase
								index === currentIndex || index === lastIndex
									? 'transition-all duration-[0.65s] ease-in-out'
									: '',
							)}
						>
							{`"${phrase.charAt(0).toUpperCase() + phrase.slice(1)}"`}
						</span>
					))}
				</span>
			</span>
		</div>
	);
};
const SVNewBoardModal = SVModal.create(
	({
		items,
		fromExtension,
		ownershipType,
		autoRedirect = true,
	}: {
		items?: Array<Pick<ItemFragmentFragment, '_id' | 'name' | 'shortID' | 'asset'>>;
		fromExtension?: boolean;
		ownershipType: ValueOf<typeof BoardOwnershipType>;
		autoRedirect?: boolean;
	}) => {
		const client = useApolloClient();
		const router = useRouter();
		const close = useModalClose();
		const [isTeamBoard] = useState(ownershipType === BoardOwnershipType.TEAM);
		const [isPrivate, setIsPrivate] = useState(ownershipType === BoardOwnershipType.TEAM);
		const [isHidden, setIsHidden] = useState(false);
		const [isTeamEditorAnEditor, setIsTeamEditorAnEditor] = useState(
			ownershipType === BoardOwnershipType.TEAM,
		);
		const [name, setName] = useState('');
		const [description, setDescription] = useState('');
		const [selectedUsers, setSelectedUsers] = useState<LikeUser[]>([]);
		const [isSubmitting, setIsSubmitting] = useState(false);
		const [error, setError] = useState<string | null>(null);
		const hasItems = items && items.length > 0;

		const { data: itemDetails, loading } = useQuery(ItemDetailsQuery, {
			variables: {
				shortID: items?.[0]?.shortID ?? '',
			},
			skip: !hasItems,
		});

		const [tags, setTags] = useState<string[]>([]);
		useEffect(() => {
			if (itemDetails) {
				setTags(itemDetails?.itemByShortID?.asset?.tags?.map((tag) => tag.name) || []);
			}
		}, [itemDetails]);

		const handleSubmit = useEventCallback(
			async (
				values: Omit<
					NewBoardMutationMutationVariables['input'],
					'itemIDs' | 'ownershipType'
				>,
			) => {
				setIsSubmitting(true);
				setError(null);

				try {
					const itemIDs = items?.map((item) => item._id) ?? [];

					const { data } = await client.mutate({
						mutation: NewBoardMutation,
						variables: {
							input: {
								itemIDs,
								ownershipType,
								...values,
								isPrivate,
								isTeamEditorAnEditor,
								isHidden,
							},
						},
					});

					if (data?.newBoard.board) {
						toast.CreateBoard(data.newBoard.board.name, {
							thumbnails: items?.[0]?.asset.image.thumbnail
								? [items[0].asset.image.thumbnail]
								: [],
						});
					}

					if (data?.newBoard.board._id && selectedUsers.length > 0) {
						const userIDs = selectedUsers
							.map((user) => !('email' in user) && '_id' in user && user._id)
							.filter(Boolean);
						const emails = selectedUsers
							.map((user) => 'email' in user && user.email)
							.filter(Boolean);

						if (ownershipType === BoardOwnershipType.USER) {
							await client.mutate({
								mutation: InviteUsersToBoardMutation,
								variables: {
									input: {
										boardID: data.newBoard.board._id,
										userIDs,
										emails,
									},
								},
							});
						}
					}

					// delay to the invite toast (to not conflict with the create board toast)
					if (selectedUsers.length > 0) {
						const mappedUsers = selectedUsers.map((user) => {
							const isEmailUser = 'email' in user;
							return {
								name: isEmailUser
									? user.email
									: (user as any).name || (user as any).username,
							};
						});

						setTimeout(() => {
							toast.success('Invite', {
								users: mappedUsers,
								content: 'Invite sent to',
								disableCloseButton: true,
							});
						}, 1000);
					}

					if (!fromExtension && data && autoRedirect) {
						router.push(data.newBoard.board.url);
					}

					close();
				} catch (error: any) {
					let errorMessage = 'An error occurred while creating the board.';

					if (error?.graphQLErrors?.[0]?.message) {
						errorMessage = error.graphQLErrors[0].message;
					} else if (error?.message) {
						errorMessage = error.message.replace(/^GraphQL error: /, '');
					}

					setError(errorMessage);
					console.error('Error creating board:', error);
				} finally {
					setIsSubmitting(false);
				}
			},
		);

		return (
			<div className="-sm:space-x-0 -sm:overflow-x-auto flex max-h-[658px] max-w-full flex-row space-x-14 overflow-x-hidden">
				{hasItems ? (
					<div className="-sm:hidden flex flex-1 flex-col">
						<div className={cn(hasItems ? 'flex flex-row' : 'flex flex-col')}>
							{hasItems ? (
								<SVImage
									src={items?.[0]?.asset.image.thumbnail}
									className="mx-auto my-auto h-full w-[300px] object-contain"
								/>
							) : null}
						</div>
					</div>
				) : null}
				<div className="flex max-h-[658px] w-[348px] flex-1 flex-col">
					<SVModal.Title className="mb-5 flex-shrink-0">
						New {isTeamBoard ? 'team ' : ''}board
					</SVModal.Title>

					{error && (
						<SVMessage use={SVMessage.USES.ERROR} className="mb-4">
							{error}
						</SVMessage>
					)}

					<div className="w-full overflow-y-auto overflow-x-hidden p-1">
						<div>
							{isTeamBoard ? (
								<SVFormLayout.P>
									Team boards enable collaboration among your entire team.
								</SVFormLayout.P>
							) : null}

							<div className="flex w-full flex-col">
								<div className="relative">
									<span className="type-label text-muted mb-2 block">
										Board name
									</span>
									<div className="relative">
										<input
											className={cn(
												'type-base bg-background input-text-default w-full rounded-lg border-0 px-4 py-3 placeholder-gray-700 ring-1 ring-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600',
											)}
											name="name"
											title="Board name"
											required={true}
											value={name || ''}
											onChange={(e) => setName(e.target.value)}
										/>

										{hasItems && !name && (
											<_AnimatedPlaceholderText tags={tags} />
										)}
									</div>
								</div>
							</div>

							<div
								className={cn(
									'mb-5 mt-5 flex justify-between',
									isTeamBoard && 'disabled',
								)}
							>
								<div className="flex flex-col text-start">
									<div className="text-base font-semibold text-gray-300">
										Private
									</div>
									<div className="type-label text-muted">
										{isTeamBoard
											? 'Team boards are always private'
											: 'Only you and collaborators can see it.'}
									</div>
								</div>
								<SVToggle
									isPressed={isPrivate}
									onClick={() => !isTeamBoard && setIsPrivate(!isPrivate)}
									isDisabled={isTeamBoard}
									dataTest="private"
									use="extension-settings"
								/>
							</div>

							{!isTeamBoard ? (
								<div className="mb-5 mt-5 flex justify-between">
									<div className="flex flex-col text-start">
										<div className="text-base font-semibold text-gray-300">
											Hide from feed
										</div>
										<div className="type-label text-muted">
											Won't show up on your profile and feed.
										</div>
									</div>
									<SVToggle
										isPressed={isHidden}
										onClick={() => setIsHidden(!isHidden)}
										use="extension-settings"
									/>
								</div>
							) : null}

							{isTeamBoard ? (
								<div className="mb-5 flex items-center justify-between">
									<div className="flex flex-col text-start">
										<div className="text-base font-semibold text-gray-300">
											Team editor can edit
										</div>
										<div className="type-label text-muted pr-2">
											If disabled, only team owners, admins and curators can
											add/remove saves from this board.
										</div>
									</div>
									<SVToggle
										isPressed={isTeamEditorAnEditor}
										onClick={() =>
											setIsTeamEditorAnEditor(!isTeamEditorAnEditor)
										}
									/>
								</div>
							) : null}

							{!isTeamBoard ? (
								<div className="mt-14">
									<SVInviteUsersContainer
										type={InviteType.BOARD}
										onSuccess={close}
										onUsersSelected={setSelectedUsers}
										newBoard={true}
									/>
								</div>
							) : null}
						</div>
					</div>
					<div
						className={cn(
							'flex justify-end font-normal',
							isTeamBoard ? 'mt-5' : 'mt-14',
						)}
					>
						<SVButton
							onClick={() =>
								handleSubmit({
									name,
									description,
									isPrivate,
									isTeamEditorAnEditor,
									isHidden,
								})
							}
							use={SVButtonUSES.PRIMARY}
							className="type-label type-label rounded-[16px] bg-gray-200 px-6 py-5"
							size={SVButtonSIZES.TINY}
							isLoading={isSubmitting}
						>
							Create board
						</SVButton>
					</div>
				</div>
			</div>
		);
	},
);

export default SVNewBoardModal;
